import React from 'react'
import { withFormik } from 'formik'
import styled from 'styled-components'
import { adjustHue, darken, lighten, saturate } from 'polished'

import { saveSignature } from 'src/api.js'
import { TextWithEllipsis } from 'src/components/AnimatedEllipsis.js'
import { BLACK, RED, WHITE } from 'src/styles/colors.js'

const FormStatus = {
  ALL_GOOD: 'ALL_GOOD',
  FAILED: 'FAILED',
}

async function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function stall(promise, ms) {
  const [result] = await Promise.all([promise, delay(ms)])
  return result
}

const FormFields = styled.div`
  display: flex;
  margin: 0 -16px 8px;
  opacity: ${props => (props.isSubmitting ? 0.4 : 1)};
  transition: opacity 0.3s;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`

const Field = styled.div`
  width: 33.333%;
  margin-bottom: 16px;
  padding: 0 16px;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
`

const Input = styled.input`
  appearance: none;
  display: block;
  width: calc(100% - 32px);
  padding: 8px 16px;
  box-shadow: none;
  outline: none;
  border: none;
  border-radius: 2px;
  color: ${BLACK};
  font-family: 'Asap', sans-serif;
  font-size: 16px;
  line-height: 24px;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: inset 0 0 0 2px #bbb;
  }

  &:focus {
    box-shadow: inset 0 0 0 2px ${RED};
  }
`

const ErrorMessage = styled.div`
  margin-top: 8px;
  color: ${RED};
  font-size: 12px;
  line-height: 16px;
`

const Button = styled.button`
  appearance: none;
  padding: 10px 56px 14px;
  background: linear-gradient(
    to right,
    ${lighten(0.05, adjustHue(8, RED))},
    ${RED}
  );
  box-shadow: none;
  outline: none;
  border: none;
  border-radius: 4px;
  box-shadow: inset 0 -4px 0 rgba(0, 0, 0, 0.2);
  color: ${WHITE};
  font-family: 'Asap', sans-serif;
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  text-shadow: 0 1px 0px rgba(0,0,0,0.5);
  transition: all 0.1s;
  cursor: pointer;
  opacity: ${props => (props.isSubmitting ? 0.75 : 1)};

  &:hover,
  &:focus {
    margin-top: -2px;
    padding-bottom: 16px;
    box-shadow: inset 0 -6px 0 rgba(0, 0, 0, 0.2);
    background: linear-gradient(
      to right,
      ${saturate(0.1, lighten(0.05, adjustHue(8, RED)))},
      ${saturate(0.1, RED)}
    );
  }

  &:active {
    margin-top: 0;
    padding-bottom: 14px;
    box-shadow: inset 0 0 0 rgba(0, 0, 0, 0);
    background: linear-gradient(
      to right,
      ${darken(0.05, adjustHue(8, RED))},
      ${darken(0.05, RED)}
    );App.js
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

const FormError = ErrorMessage.extend`
  margin: 0 0 24px;
  font-size: 16px;
  line-height: 24px;
`

const InnerForm = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  submitCount,
  status,
  isValid,
  isSubmitting,
}) => (
  <form onSubmit={handleSubmit}>
    <FormFields isSubmitting={isSubmitting}>
      <Field>
        <Label htmlFor="name">Namn</Label>
        <Input
          type="name"
          name="name"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
        />
        {touched.name &&
          errors.name &&
          submitCount > 0 && <ErrorMessage>{errors.name}</ErrorMessage>}
      </Field>

      <Field>
        <Label htmlFor="city">Ort</Label>
        <Input
          type="city"
          name="city"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.city}
        />
        {touched.city &&
          errors.city &&
          submitCount > 0 && <ErrorMessage>{errors.city}</ErrorMessage>}
      </Field>

      <Field>
        <Label htmlFor="email">E-post</Label>
        <Input
          type="email"
          name="email"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.email}
        />
        {touched.email &&
          errors.email &&
          submitCount > 0 && <ErrorMessage>{errors.email}</ErrorMessage>}
      </Field>
    </FormFields>

    {status === FormStatus.FAILED && (
      <FormError>
        Något gick snett, så vi kunde inte spara din signatur. Prova igen om en
        liten stund!
      </FormError>
    )}

    <Button type="submit" isSubmitting={isSubmitting}>
      {isSubmitting ? (
        <TextWithEllipsis>Skickar underskrift</TextWithEllipsis>
      ) : (
        'Skriv under'
      )}
    </Button>
  </form>
)

const Form = withFormik({
  mapPropsToValues: () => ({
    name: '',
    city: '',
    email: '',
  }),
  validate: values => {
    const errors = {}

    if (values.name === '') {
      errors.name = 'Du måste fylla i ditt namn'
    }
    if (values.city === '') {
      errors.city = 'Du måste fylla i orten du bor på'
    }
    if (values.email === '') {
      errors.email = 'Du måste fylla en e-postadress'
    }

    return errors
  },
  handleSubmit: async (values, { props, setStatus, setSubmitting }) => {
    const results = await stall(saveSignature(values), 2000)

    setSubmitting(false)

    if (results.status === 'ok') {
      setStatus(FormStatus.ALL_GOOD)
      props.onSign(values)
    } else {
      setStatus(FormStatus.FAILED)
    }
  },
})(InnerForm)

export default Form
